import { getTheme, Icon, IStyle, IStyleFunctionOrObject, ITextFieldStyleProps, ITextFieldStyles, ITheme, mergeStyleSets, MessageBarType } from "@fluentui/react";
import { BwjButton, BwjButtonType } from "../../../../framework/components/bwj-button";
import { BwjPageContent } from "../../../../framework/components/bwj-page-content";
import { BwjPalette } from "../../../../app-core/app-theme";
import * as formService from '../../form-services/shop-signin';
import { BwjTextField } from "../../../../framework/components/bwj-text-field";
import { BwjViewManager, BwjViewState } from "../../../../framework/components/bwj-view-manager";
import { useEffect, useMemo, useState } from "react";
import { IShowAnnouncementBannerArgs } from "../../../../framework/components/bwj-announcement/props";
import { BwjAnnouncementBanner } from "../../../../framework/components/bwj-announcement";
import { CustomerAccountService } from "../../../../services/customer-account";
import { useNavigator } from "../../../../framework/services/navigation";
import { AppRoutes, getNextShoppingUrl } from "../../../app-routes";
import { BwjConfirmationDialog, IConfirmationDialogDisplayProps } from "../../../../framework/components/bwj-confirmation-dialog";
import { useParams } from "react-router-dom";
import { IShopSignInEditingModel } from "../../entities/sign-in-editing-model";
import { BwjEventArgs } from "../../../../framework/entities/bwj-event";
import { ToastService } from "../../../../framework/services/toast";

const getClasses = (theme: ITheme) => {
  const main: IStyle = {
    margin: 0,
    marginTop: '2em',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: theme.palette.white,
    fontSize: theme.fonts.large.fontSize,
    fontWeight: 400,
  };

  const cart: IStyle = {
    fontSize: 100,
  };

  const tagline: IStyle = {
    fontSize: theme.fonts.xxLarge.fontSize,
    fontWeight: 'bold',
    textAlign: 'center',
    margin: 0,
    marginTop: '0.5em',
    marginBottom: '1em',
  };

  const label: IStyle = {
    display: 'block',
    marginBottom: '0.65em',
  };

  const textFieldContainer: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '2rem',
  };

  const helpContainer: IStyle = {
    width: '100%',
    maxWidth: 400,
    margin: 0,
    marginTop: '2rem',
    textAlign: 'center',

    '& > a': {
      display: 'block',
      marginBottom: '2rem',
      color: theme.palette.white,
      textDecoration: 'underline',
    },
    '& > a:hover': {
      color: theme.palette.themePrimary,
    },
  };

  return mergeStyleSets({
    main,
    cart,
    tagline,
    label,
    textFieldContainer,
    helpContainer,
  });
}

const startButtonStyles = {
  root: {
    fontSize: '1em',
    width: '100%',
    maxWidth: 400,
  },
  label: {
    fontWeight: 'bold',
  },
};

const textFieldStyles = (theme:ITheme) => {
  return {
    root: {
      fontSize: theme.fonts.large.fontSize,
      maxWidth: '10rem',
    },
    field: {
      fontSize: theme.fonts.large.fontSize,
      fontWeight: 700,
      textAlign: 'center',
    }, 
    fieldGroup: {
      height: '2em'
    },
    errorMessage: {
      backgroundColor: 'rgba(255,255,255,0.55)',
      padding: 5,
    },
  };
};

const verificationFailedArgs: IShowAnnouncementBannerArgs = {
  messageType: MessageBarType.error,
  message: 'Sorry &ndash; we couldn&#39;t find a catalog with the code and/or house number you provided.  Please double-check, and try again.  If you need help, call us at <a href="tel:5855761654">(585) 576-1654</a>, or use our <a href="//bwjstorm.com/contact">contact form</a>.',
  dismissable: true,
  renderAsHtml: true
};

const subscriberId = 'Page.Start';

export const PageStart: React.FunctionComponent = () => {
  const theme = getTheme();
  const classes = getClasses(theme);
  const { code } = useParams<{code: string}>();
  const nav = useNavigator();
  const textFieldStyle = textFieldStyles(theme) as unknown as IStyleFunctionOrObject<ITextFieldStyleProps, ITextFieldStyles>;
  const formModel = formService.useFormModel(subscriberId);
  const [viewState, setViewState] = useState(BwjViewState.Ready);
  const [confirmSignOutArgs, setConfirmSignOutArgs] = useState<BwjEventArgs<IConfirmationDialogDisplayProps | undefined> | undefined>(undefined);
  const showAnnouncementArgs = useMemo(() => {
    return formModel.verificationFailed ? new BwjEventArgs({ ...verificationFailedArgs }) : undefined;
  }, [formModel]);

  useEffect(() => {
    if(CustomerAccountService.isAuthenticated()) {
      const confirmArgs = {
        title: 'Close Catalog?',
        message: 'You already have a catalog open - do you want to close this catalog and open another?',
        proceedPrompt: 'Yes, close catalog',
        cancelPrompt: 'Cancel',
        onProceedRequested: () => {
          CustomerAccountService.signOut();
        },
        onCancelRequested: () => {
          nav.goTo(AppRoutes.home.url());
        },
        context: undefined,
      };
      setConfirmSignOutArgs(new BwjEventArgs(confirmArgs));
    }

    const dataModel: IShopSignInEditingModel = {
      catalogCode: code ?? '',
    };
    formService.initializeService(dataModel);
  }, [nav, code]);

  const onVerifyCredentials = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return undefined; }

      setViewState(BwjViewState.Loading);
      const request = formService.toStartRequest();
      return CustomerAccountService.signIn(request);
    })
    .then(resp => {
      if(resp === undefined) { return; }

      if(resp.success) {
        CustomerAccountService.getProjectConfiguration()
        .then(pc => {
          const url = getNextShoppingUrl(pc!);
          nav.goTo(url);
        });
      } else {
        setViewState(BwjViewState.Ready);
        formService.verificationFailed.onUpdate(true);
      }
    })
    .catch(() => {
      setViewState(BwjViewState.Ready);
      ToastService.show(MessageBarType.error, 'Whoa... Unexpected error; please try again');
    });
  }

  return (
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Getting your catalog...',
      }}
    >
      <>
      <BwjConfirmationDialog
        displayArgs={confirmSignOutArgs}
        closeButtonAriaLabel={'Keep current catalog open'}
      />
      <BwjAnnouncementBanner
        invokeShowAnnouncement={showAnnouncementArgs}
        closeDialogButtonText='Close'
        ariaCloseBannerMessage='Dismiss notification'
        ariaShowFullMessageDialog='View full notification'
      />
      <BwjPageContent
        contentDescription='primary content (no subcontent)'
        backgroundColor={BwjPalette.accentPrimary}
        style={{width: '100%', boxSizing: 'border-box', }}
      >
        <div className={classes.main}>
          <Icon iconName='ShoppingCart' className={classes.cart} aria-hidden />
          <h2 className={classes.tagline}>{'Let\'s find a new door!'}</h2>
          <div className={classes.textFieldContainer}>
            <label
              htmlFor={formService.catalogCode.id}
              className={classes.label}
            >
              {formService.catalogCode.description}
            </label>
            <BwjTextField
              id={formService.catalogCode.id}
              required={false}
              onUpdate={formService.catalogCode.onUpdate}
              input={formModel.catalogCode}
              filter={/^[a-z0-9]{0,4}$/i}
              styles={textFieldStyle}
            />
          </div>
          <div className={classes.textFieldContainer}>
            <label
              htmlFor={formService.houseNumber.id}
              className={classes.label}
            >
              {formService.houseNumber.description}
            </label>
            <BwjTextField
              id={formService.houseNumber.id}
              required={false}
              onUpdate={formService.houseNumber.onUpdate}
              input={formModel.houseNumber}
              filter={/^[a-z0-9 -]{0,15}$/i}
              styles={textFieldStyle}
            />
          </div>
          <BwjButton
            buttonStyle={BwjButtonType.PrimaryStandard}
            styles={startButtonStyles}
            text={'Start shopping'}
            onClick={onVerifyCredentials}
          />
          <div className={classes.helpContainer}>
            <a href="//bwjstorm.com/request-measurement">I don&#39;t have a catalog code</a>
            <a href="//bwjstorm.com/contact">I lost my catalog code</a>
          </div>
        </div>
      </BwjPageContent>
      </>
    </BwjViewManager>
  );
};
